<script lang="ts">
  import { addCredits } from '../lib/components/map/credits';
  import { watchSight } from '../lib/utils/sight';
  import { onDestroy, onMount } from 'svelte';
  import * as maplibregl from 'maplibre-gl';
  import { api } from '../lib/api/client';
  import type { Fix, MapInfo } from '../lib/api/types';
  import { age } from '../lib/utils/time';
  import { initials } from '../lib/utils/geo';
  import { wakeCause } from '../lib/utils/memberState';
  import { clockAt } from '../lib/utils/checkin';
  import { followTheme, northUpMap, styleFor } from '../lib/components/map/theme';
  import { integer, t } from '../lib/i18n/index.ts';

  // Share is a share link as anyone who opens it sees it, without an account
  // (spec 001 FR-087, 001 FR-088): the member's name, their latest location
  // with its age, their trail since the link was made, and when it ends. While
  // it is active the page follows the member live, as a group follower does.
  let { token }: { token: string } = $props();

  interface ShareView {
    display_name: string;
    active: boolean;
    ends_at: string;
    place_name?: string;
    latest?: Fix;
    trail: Fix[];
    map?: MapInfo;
    quiet?: boolean;
    live_state?: string;
    live_reason?: string;
  }

  let view = $state<ShareView | null>(null);
  let error = $state('');
  let live = $state<{ state: string; reason?: string } | null>(null);
  let mapEl: HTMLDivElement | undefined = $state();
  let map: maplibregl.Map | null = null;
  let marker: maplibregl.Marker | null = null;
  let stopTheme: (() => void) | null = null;
  let poll: ReturnType<typeof setTimeout> | null = null;
  let rejoin: ReturnType<typeof setInterval> | null = null;
  let now = $state(Date.now());
  const tick = setInterval(() => (now = Date.now()), 15_000);

  // A page hidden for 2 minutes stops following, as a follower's does, so a
  // forgotten tab does not keep the member's GPS on (constitution I); it
  // follows again when it shows.
  let away = false;
  const stopWatchingSight = watchSight(
    () => {
      away = true;
      if (rejoin) clearInterval(rejoin);
      rejoin = null;
      live = null;
      api.delete(`/share/${token}/live`).catch(() => {});
    },
    () => {
      away = false;
      if (poll) clearTimeout(poll);
      load();
    },
  );

  onMount(async () => {
    await load();
  });

  onDestroy(() => {
    stopWatchingSight();
    clearInterval(tick);
    if (poll) clearTimeout(poll);
    if (rejoin) clearInterval(rejoin);
    if (view?.active) api.delete(`/share/${token}/live`).catch(() => {});
    stopTheme?.();
    map?.remove();
  });

  // show puts the member on the map, making the map the first time there is
  // a location to center it on.
  function show(v: ShareView) {
    const at = v.latest;
    if (!at || !mapEl) return;
    if (!map) {
      const info = v.map ?? null;
      map = northUpMap({ container: mapEl, style: styleFor(info), center: [at.lon, at.lat], zoom: 15, attributionControl: false });
      addCredits(map, info, 'share');
      stopTheme = followTheme(map, info, draw);
    }
    if (!marker) {
      const el = document.createElement('div');
      el.className = 'share-marker';
      el.textContent = initials(v.display_name);
      marker = new maplibregl.Marker({ element: el }).setLngLat([at.lon, at.lat]).addTo(map);
    } else {
      marker.setLngLat([at.lon, at.lat]);
      map.easeTo({ center: [at.lon, at.lat], duration: 500 });
    }
    draw();
  }

  // load reads the link, and reads it again soon: every 3 seconds while the
  // phone streams, else every 20; a failed read is tried again, and only a
  // link the server does not know stops it.
  async function load() {
    try {
      const v = await api.get<ShareView>(`/share/${token}`);
      view = v;
      error = '';
      if (!v.active) {
        // An ended link shows nothing of where the member was (001 FR-089).
        if (rejoin) clearInterval(rejoin);
        rejoin = null;
        live = null;
        // The map goes too: left centred, it would still say where they were.
        marker = null;
        stopTheme?.();
        stopTheme = null;
        map?.remove();
        map = null;
        return;
      }
      show(v);
      // Each read carries the session's state, so the page says at once
      // when the phone streams or cannot be reached.
      if (v.live_state) live = { state: v.live_state, reason: v.live_reason };
      else if (live) {
        // The session ended under the page, as when no one else followed:
        // it says so and joins again at once rather than at the next minute.
        live = null;
        if (!v.quiet && !away && rejoin) await follow();
      }
      // A phone that reports only when its member shares cannot stream.
      if (!v.quiet && !rejoin && !away) {
        await follow();
        rejoin = setInterval(follow, 60_000);
      } else if (v.quiet && rejoin) {
        clearInterval(rejoin);
        rejoin = null;
      }
    } catch (e: any) {
      if (e.code === 'NOT_FOUND') {
        error = e.message;
        return;
      }
    }
    // Quick while the phone streams or is being woken, slow otherwise.
    poll = setTimeout(load, !away && (live?.state === 'streaming' || live?.state === 'waiting') ? 3_000 : 20_000);
  }

  async function follow() {
    try {
      live = await api.post<{ state: string; reason?: string }>(`/share/${token}/live`);
    } catch {
      live = null;
    }
  }

  function draw() {
    if (!map || !map.isStyleLoaded() || !view) return;
    const data: GeoJSON.Feature = { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: view.trail.map((f) => [f.lon, f.lat]) } };
    const src = map.getSource('share-trail') as maplibregl.GeoJSONSource | undefined;
    if (src) {
      src.setData(data);
      return;
    }
    map.addSource('share-trail', { type: 'geojson', data });
    map.addLayer({ id: 'share-trail', type: 'line', source: 'share-trail', layout: { 'line-join': 'round', 'line-cap': 'round' }, paint: { 'line-color': '#1e66f5', 'line-width': 4, 'line-opacity': 0.8 } });
  }

  // liveWords says how following goes; a state the page does not know shows
  // as the server named it.
  function liveWords(l: { state: string; reason?: string }): string {
    switch (l.state) {
      case 'waiting': return t('shareLiveWaiting');
      case 'streaming': return t('shareLiveStreaming');
      case 'unreachable': return l.reason ? t('shareLiveUnreachableBecause', { cause: wakeCause(l.reason) }) : t('shareLiveUnreachable');
      default: return l.state;
    }
  }
</script>

<div class="flex flex-1 min-h-0 flex-col md:flex-row">
  <aside class="md:w-96 shrink-0 border-b md:border-b-0 md:border-r p-5 space-y-2" style="border-color: var(--theme-surface0); background: var(--theme-mantle);">
    {#if error}
      <p style="color: var(--theme-error);">{t('shareNotFound')}</p>
    {:else if !view}
      <p class="text-text-muted">{t('shareLoading')}</p>
    {:else if !view.active}
      <h1 class="text-xl font-semibold">{t('shareStopped', { member: view.display_name })}</h1>
      <p class="text-sm text-text-muted">{t('shareEnded', { age: age(view.ends_at) })}</p>
    {:else}
      <h1 class="text-xl font-semibold">{t('shareTitle', { member: view.display_name })}</h1>
      <p class="text-sm text-text-muted">
        {view.place_name
          ? t('shareUntilPlace', { member: view.display_name, place: view.place_name, time: clockAt(view.ends_at) })
          : t('shareUntil', { member: view.display_name, time: clockAt(view.ends_at) })}
      </p>
      {#key now}
        {#if view.latest}
          <p class="text-sm">{t('shareLatest', { age: age(view.latest.recorded_at), accuracy: integer(Math.round(view.latest.accuracy_m)) })}</p>
        {:else}
          <p class="text-sm">{t('shareUnknown')}</p>
        {/if}
      {/key}
      {#if view.quiet}
        <p class="text-sm text-text-muted">{t('shareQuiet', { member: view.display_name })}</p>
      {:else if live}
        <p class="text-sm text-text-muted">
          <i aria-hidden="true" class="las la-broadcast-tower mr-1"></i>{liveWords(live)}
        </p>
      {/if}
    {/if}
    <p class="text-xs text-text-muted pt-2">{t('shareFooter')}</p>
  </aside>
  <div class="flex-1 relative min-w-0 min-h-80">
    <div bind:this={mapEl} class="absolute inset-0"></div>
  </div>
</div>

<style>
  :global(.share-marker) {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 9999px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 0.8rem;
    background: var(--theme-primary, #1e66f5);
    color: var(--theme-base);
    border: 3px solid var(--theme-base);
    box-shadow: var(--theme-shadow-md);
  }
</style>
